/**
 * A container image reference, split into the parts a tenant needs to see.
 *
 * This is a subset of the Docker reference grammar — enough to label the
 * parts and to say whether the reference is pinned — not a validator. The
 * registry is never contacted: whether the tag exists is the registry's answer
 * to give at placement time, not this form's.
 *
 * The rule that matters is where the tag starts. A `:` in the first path
 * component is a registry port, not a tag, so `localhost:5000/app` has no tag
 * and runs `latest`; the check this replaced treated any `:` as a pin.
 */
export type ImageRef = {
  /** `docker.io` when the reference names no registry. */
  registry: string
  /** `Docker Hub` for docker.io, otherwise the registry host as typed. */
  registryLabel: string
  /** As typed, not normalised: `nginx`, not `library/nginx`. */
  repository: string
  /** Null when the reference carries no tag, which means `latest`. */
  tag: string | null
  /** `sha256:…` when the reference carries a digest. */
  digest: string | null
  /** A digest pins; so does any explicit tag other than `latest`. */
  pinned: boolean
}

const DOCKER_HUB = 'docker.io'

/** Null for an empty, whitespace-only or obviously malformed reference. */
export function parseImageRef(input: string): ImageRef | null {
  const value = input.trim()
  if (value === '' || /\s/.test(value)) return null

  const at = value.indexOf('@')
  const name = at === -1 ? value : value.slice(0, at)
  const digest = at === -1 ? null : value.slice(at + 1)
  if (digest !== null && !/^[a-z0-9]+(?:[+._-][a-z0-9]+)*:[a-f0-9]{32,}$/i.test(digest)) return null

  const slash = name.indexOf('/')
  const first = slash === -1 ? '' : name.slice(0, slash)
  const hasRegistry = slash !== -1 && (first.includes('.') || first.includes(':') || first === 'localhost')
  const registry = hasRegistry ? first : DOCKER_HUB
  const path = hasRegistry ? name.slice(slash + 1) : name

  // With the registry split off, the last `:` can only start a tag.
  const colon = path.lastIndexOf(':')
  const tag = colon === -1 || path.slice(colon + 1).includes('/') ? null : path.slice(colon + 1)
  const repository = tag === null ? path : path.slice(0, colon)
  if (repository === '' || tag === '' || repository.split('/').some((part) => part === '')) return null

  return {
    registry,
    registryLabel: registry === DOCKER_HUB ? 'Docker Hub' : registry,
    repository,
    tag,
    digest,
    pinned: digest !== null || (tag !== null && tag !== 'latest'),
  }
}
